import { useEffect, memo, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { FlexRow } from 'components/layout/FlexRow';
import {
  MonitoringColors,
  PassedColors,
  QualifiedColors,
  type SavedStateColorScheme,
} from 'components/widgets/SaveProject/savedStateColors';
import { useSelectedLeadFilters } from 'hooks/useLeadTableFiltering';
import { SummaryDataKeys, useProjectData } from 'hooks/useProjectsByCategory';
import { useSelectedCollectionQuery, useSelectedGroupQuery } from 'state/queryParams';
import { FilterViewEnum } from 'types/FilterProjects';
import { trackFilterEvent } from 'utils/analytics';
import { formatInteger } from 'utils/format';

const container = {
  display: 'flex',
  alignItems: 'center',
  background: 'transparent',
  gap: 'padding_8',
};

const SCHEME_BY_VALUE: Partial<Record<FilterViewEnum, SavedStateColorScheme>> = {
  [FilterViewEnum.STAR]: QualifiedColors,
  [FilterViewEnum.WATCH]: MonitoringColors,
  [FilterViewEnum.HIDDEN]: PassedColors,
};

const getBorderColor = (isActive: boolean, scheme?: SavedStateColorScheme) => {
  if (scheme) {
    return scheme.border;
  }

  return isActive ? Colors.ForestGreen900 : Colors.ArmyGreen200;
};

const buttonStyle = (isActive: boolean, scheme?: SavedStateColorScheme) => ({
  borderRadius: '24px',
  marginRight: 0,
  letterSpacing: '-0.13px',
  border: `1px solid ${getBorderColor(isActive, scheme)}`,
});

const contentStyle = (scheme?: SavedStateColorScheme) => ({
  justifyContent: 'space-between',
  gap: 'padding_8',
  color: scheme ? scheme.foreground : Colors.Black800,
});

const FILTER_TABS = [
  { display: 'View all', value: FilterViewEnum.ALL },
  { display: 'No status', value: FilterViewEnum.NO_STATUS },
  { display: 'Qualified', value: FilterViewEnum.STAR },
  { display: 'Monitoring', value: FilterViewEnum.WATCH },
  { display: 'Passed', value: FilterViewEnum.HIDDEN },
];

type Props = {
  visibleTabs?: FilterViewEnum[];
  collectionOrGroup: 'collection' | 'group';
};

const CategoryFilters = ({
  visibleTabs = Object.values(FilterViewEnum),
  collectionOrGroup,
}: Props) => {
  const { selectedLeadFilters: activeTabs, setSelectedLeadFilters: setActiveTabs } =
    useSelectedLeadFilters();
  const [group] = useSelectedGroupQuery();
  const [collectionId] = useSelectedCollectionQuery();
  const {
    getGroupConfigLeadsSummary,
    getCollectionSummary,
    invalidateGetGroupConfigLeadSummary,
    invalidateGetCollectionSummary,
  } = useProjectData();
  const { data: groupData } = getGroupConfigLeadsSummary(Number(group));
  const { data: collectionData } = getCollectionSummary(Number(collectionId));

  const data = collectionOrGroup === 'collection' ? collectionData : groupData;

  const isTabActive = (tab: FilterViewEnum) => {
    return activeTabs.includes(tab);
  };

  const toggleTab = (tab: FilterViewEnum) => {
    let updatedTabs: FilterViewEnum[] = [...activeTabs];

    if (tab === FilterViewEnum.ALL) {
      updatedTabs = [FilterViewEnum.ALL];
    } else if (isTabActive(FilterViewEnum.ALL)) {
      updatedTabs = [tab];
    } else if (activeTabs.includes(tab)) {
      updatedTabs = updatedTabs.filter((t) => t !== tab);
    } else {
      updatedTabs.push(tab);
    }

    // Sort the active tabs in alphabetical order
    const sortedTabs = updatedTabs.sort().join(' ');

    setActiveTabs(updatedTabs);

    // Send the space-delimited string of active filters to trackFilterEvent
    trackFilterEvent(sortedTabs);
  };

  const handleToggleTab = (tabValue: FilterViewEnum) => () => {
    toggleTab(tabValue);
  };

  const getIconNameForTab = (tab: FilterViewEnum) => {
    switch (tab) {
      case FilterViewEnum.WATCH:
        return Icon.Name.Eye;
      case FilterViewEnum.STAR:
        return Icon.Name.Star;
      case FilterViewEnum.HIDDEN:
        return Icon.Name.EyeHide;
      default:
        return null;
    }
  };

  const counts = useMemo(() => {
    const keySuffix = collectionOrGroup === 'collection' ? '_projects' : '_leads';

    const totalKey = `number_of${keySuffix}` as SummaryDataKeys;
    const starKey = `number_of_star${keySuffix}` as SummaryDataKeys;
    const watchKey = `number_of_watch${keySuffix}` as SummaryDataKeys;
    const hiddenKey = `number_of_hidden${keySuffix}` as SummaryDataKeys;

    const total = data?.[totalKey as SummaryDataKeys] || 0;
    const star = data?.[starKey] || 0;
    const watch = data?.[watchKey] || 0;

    return {
      [FilterViewEnum.ALL]: total,
      [FilterViewEnum.NO_STATUS]:
        Number(data?.[totalKey] || 0) -
          Number(data?.[starKey] || 0) -
          Number(data?.[watchKey] || 0) || 0,
      [FilterViewEnum.STAR]: star,
      [FilterViewEnum.WATCH]: watch,
      [FilterViewEnum.HIDDEN]: data?.[hiddenKey] || 0,
    };
  }, [data]);

  const renderTabButton = (tab: { display: string; value: FilterViewEnum }) => {
    const count = counts[tab.value] || 0;

    const icon = getIconNameForTab(tab.value);
    const isActive = activeTabs.includes(tab.value);
    const scheme = SCHEME_BY_VALUE[tab.value];

    const getBackgroundColor = () => {
      if (scheme) {
        return isActive ? scheme.background : scheme.backgroundMuted;
      }

      return isActive ? Colors.ForestGreen200 : 'transparent';
    };

    const getIconFill = () => {
      if (scheme) {
        return scheme.foreground;
      }

      return isActive ? Colors.Black800 : Colors.TextQuiet;
    };

    const backgroundColor = getBackgroundColor();
    const iconFill = getIconFill();

    return (
      <Button
        key={tab.value}
        sx={buttonStyle(isActive, scheme)}
        variant={'outline' as any}
        backgroundColor={backgroundColor}
        _hover={{
          backgroundColor: scheme ? scheme.background : Colors.ForestGreen200,
        }}
        onClick={handleToggleTab(tab.value)}
      >
        <FlexRow sx={contentStyle(scheme)}>
          {icon && <Icon name={icon} fill={iconFill} />}
          {isActive ? (
            <Text.BodyBold>{tab.display}</Text.BodyBold>
          ) : (
            <Text.Body>{tab.display}</Text.Body>
          )}
          <Text.SmallLabel>{formatInteger(Number(count))}</Text.SmallLabel>
        </FlexRow>
      </Button>
    );
  };

  const displayedTabs = useMemo(
    () => FILTER_TABS.filter((tab) => visibleTabs.includes(tab.value)),
    [visibleTabs],
  );

  useEffect(() => {
    return () => {
      setActiveTabs([FilterViewEnum.ALL]);
      invalidateGetGroupConfigLeadSummary();
      invalidateGetCollectionSummary();
    };
  }, [group]);

  return <Box sx={container}>{displayedTabs.map(renderTabButton)}</Box>;
};

export default memo(CategoryFilters);
